import type { AnimationCtx, AutoRefreshCtx, TimeContextUnion } from "@/models/time-control/timeStateCtxTypes";
import {
  type TimeEventUnion,
  type TimeStateMachine,
  TimeStates,
  type TimeTypestate,
} from "@/models/time-control/timeStateTypes";
import { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { DateTime } from "luxon";
import { createMachine } from "xstate";
import {
  autoMoveInTime,
  autoShiftAnimationRange,
  clearAnimationConfig,
  constructAnimationConfig,
  constructDefaultAnimationConfig,
  ensureAnimationDefaults,
  snapAnimationWindowOnEntry,
  snapAutoRefreshTimeOnEntry,
  stashAutoRefreshDisplayTime,
  toggleLockMode,
  updateDisplayTime,
  updateFullAnimationConfig,
  updateGuiTimezone,
  updateNextDisplayTime,
  updateRefreshRateAnim,
  updateRefreshRateAuto,
} from "./timeStateActions";

const defaultCtx: AutoRefreshCtx = {
  displayTime: DateTime.utc(),
  nextDisplayTime_: DateTime.utc(),
  timezone: GuiTimeZone.utc,
  modeOpts: { refreshRate: 0, isLocked: false },
};

const defaultState = TimeStates.AUTO_REFRESH;

// Note: Keep this file as minimal as possible, so we can import this file's code
// to an online visualizer directly for development purposes (https://stately.ai/)
export const createTimeStateMachine = (
  initialCtx: TimeContextUnion = defaultCtx,
  initialStateName: string = defaultState,
): TimeStateMachine => {
  return createMachine<TimeContextUnion, TimeEventUnion, TimeTypestate>(
    {
      id: "TimeStateMachine",
      initial: initialStateName,
      states: {
        [TimeStates.ANIMATION]: {
          on: {
            // Re-enter ANIMATION on demand (e.g. after rehydrate) to trigger `entry`/`after`.
            // This allows `useInitStateTrigger` to send `setAnimationMode` even when already in ANIMATION.
            initAnimationMode: {
              target: TimeStates.ANIMATION, // external self-transition → runs `entry` again
            },
            updateAnimationMode: {
              internal: true,
              // TODO: Maybe we can strictly type different context shapes using this:
              // https://xstate.js.org/docs/guides/models.html#createmodel
              actions: [constructAnimationConfig],
            },

            setAutoRefreshMode: {
              target: TimeStates.AUTO_REFRESH,
              actions: [clearAnimationConfig],
            },
            updateDisplayTime: {
              internal: true,
              actions: [updateDisplayTime],
            },
            updateNextDisplayTime: {
              internal: true,
              actions: [updateNextDisplayTime],
            },
            updateGuiTimezone: {
              internal: true,
              actions: [updateGuiTimezone],
            },
            updateFullAnimationConfig: {
              internal: true,
              actions: [updateFullAnimationConfig],
            },
            updateRefreshRate: {
              target: TimeStates.ANIMATION,
              actions: [updateRefreshRateAnim],
            },
            toggleLockMode: {
              internal: true,
              actions: [toggleLockMode],
            },
          },
          // Ensure the animation context has a complete window shape (start/end/temporalResolution/animationTime_)
          // before snapping. Rehydrate or legacy saves may miss fields; this makes entry idempotent and safe.
          entry: [ensureAnimationDefaults, snapAnimationWindowOnEntry],
          after: {
            // WINDOW_SHIFT_INTERVAL: periodic window shift. In ANIMATION it shifts [startTime, endTime]
            // after the given interval and keeps animationTime_/displayTime inside the new window.
            WINDOW_SHIFT_INTERVAL: {
              target: TimeStates.ANIMATION,
              actions: [autoShiftAnimationRange],
            },
          },
        },

        [TimeStates.AUTO_REFRESH]: {
          on: {
            initAutoRefreshMode: {
              target: TimeStates.AUTO_REFRESH,
            },
            setAnimationMode: {
              target: TimeStates.ANIMATION,
              actions: [stashAutoRefreshDisplayTime, constructDefaultAnimationConfig],
            },
            updateGuiTimezone: {
              internal: true,
              actions: [updateGuiTimezone],
            },
            updateDisplayTime: {
              internal: true,
              actions: [updateDisplayTime],
            },
            updateNextDisplayTime: {
              internal: true,
              actions: [updateNextDisplayTime],
            },
            updateRefreshRate: {
              target: TimeStates.AUTO_REFRESH,
              actions: [updateRefreshRateAuto],
            },
            toggleLockMode: {
              internal: true,
              actions: [toggleLockMode],
            },
          },
          // Delayed self-transition.
          // We refresh the display time on entry to the state,
          // and we trigger the re-entry periodically to achive auto refresh.
          // Ref: https://xstate.js.org/docs/guides/delays.html#delayed-transitions
          entry: [snapAutoRefreshTimeOnEntry],
          after: {
            // this is only triggered by useInitStateTrigger that calls setAutoRefreshMode.
            // because of that it also needs the event on the state
            // WINDOW_SHIFT_INTERVAL: periodic window shift. In AUTO_REFRESH it advances the current
            // displayTime by `refreshRate` minutes after each interval (no animation window involved).
            WINDOW_SHIFT_INTERVAL: {
              target: TimeStates.AUTO_REFRESH,
              actions: [autoMoveInTime],
            },
          },
        },
      },
      schema: {
        context: {} as TimeContextUnion,
        events: {} as TimeEventUnion,
      },
      context: initialCtx,
      predictableActionArguments: true,
      preserveActionOrder: true,
    },
    {
      delays: {
        // WINDOW_SHIFT_INTERVAL drives the periodic time-window updates across modes.
        // In AUTO_REFRESH it advances `displayTime` by `refreshRate` minutes.
        // In ANIMATION it triggers a shift of [startTime, endTime] by `refreshRate` minutes.
        WINDOW_SHIFT_INTERVAL: (context: AutoRefreshCtx | AnimationCtx, event) => {
          const minutes = context.modeOpts?.refreshRate ?? 0;
          // fallback: 24h
          return minutes > 0 ? minutes * 60 * 1000 : 24 * 60 * 60 * 1000;
        },
      },
    },
  );
};

export const defaultTimeStateMachine = createTimeStateMachine();
